import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Numbered pagination — a minimal accessible control, tabular figures,
 * no shortcut ellipsis until >7 pages.
 *
 * Layout (owner 2026-02-01):
 *   « First  |  ‹ Previous  |  page chips  |  Next ›  |  Last »
 *
 * The double-arrow chips (« / ») jump to the first/last page and are
 * disabled at the edges alongside their single-arrow siblings. Every
 * paginated surface that uses this component picks up the behaviour
 * with no extra wiring.
 *
 * Props:
 *   page          current 1-indexed page
 *   totalPages    integer >= 0
 *   onChange(n)   called with the target page number
 *   testId        overrides `data-testid` root; defaults to "pagination"
 */
export default function Pagination({ page, totalPages, onChange, testId = "pagination" }) {
  if (!totalPages || totalPages <= 1) return null;
  const p = Math.max(1, Math.min(page, totalPages));
  const pages = pageNumbers(p, totalPages);

  return (
    <nav
      data-testid={testId}
      className="mt-8 flex items-center justify-center gap-1.5"
      aria-label="Pagination"
    >
      <PageBtn
        disabled={p <= 1}
        onClick={() => onChange(1)}
        testId={`${testId}-first`}
        aria="First page"
      >
        <ChevronsLeft size={16} />
      </PageBtn>
      <PageBtn
        disabled={p <= 1}
        onClick={() => onChange(p - 1)}
        testId={`${testId}-prev`}
        aria="Previous page"
      >
        <ChevronLeft size={16} />
      </PageBtn>
      {pages.map((n, i) =>
        n === "..." ? (
          <span
            key={`gap-${i}`}
            className="px-1 text-meta text-ink-muted tabular"
          >
            …
          </span>
        ) : (
          <PageBtn
            key={n}
            active={n === p}
            onClick={() => onChange(n)}
            testId={`${testId}-page-${n}`}
            aria={`Page ${n}`}
          >
            <span className="tabular">{n}</span>
          </PageBtn>
        )
      )}
      <PageBtn
        disabled={p >= totalPages}
        onClick={() => onChange(p + 1)}
        testId={`${testId}-next`}
        aria="Next page"
      >
        <ChevronRight size={16} />
      </PageBtn>
      <PageBtn
        disabled={p >= totalPages}
        onClick={() => onChange(totalPages)}
        testId={`${testId}-last`}
        aria="Last page"
      >
        <ChevronsRight size={16} />
      </PageBtn>
    </nav>
  );
}

function PageBtn({ children, onClick, disabled, active, testId, aria }) {
  const base = "inline-flex h-8 min-w-[32px] items-center justify-center rounded-chip border px-2 text-meta transition-colors duration-cc ease-cc-ease";
  const activeCss = active
    ? "border-indigo-text bg-indigo-chip-bg text-indigo-chip-text"
    : "border-ink-chip bg-ink-raised text-ink-muted hover:border-ink-muted hover:text-ink-text";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? "page" : undefined}
      aria-label={aria}
      className={`${base} ${activeCss} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

// Compact page list — never more than 7 chips visible.
function pageNumbers(page, total) {
  const out = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) out.push(i);
    return out;
  }
  out.push(1);
  if (page > 3) out.push("...");
  for (let i = Math.max(2, page - 1); i <= Math.min(total - 1, page + 1); i++) {
    out.push(i);
  }
  if (page < total - 2) out.push("...");
  out.push(total);
  return out;
}
